import { CookieOptionsT } from '@/types/common';
import { StorageKeyT } from '@/constants/storageKeys';

export const setCookie = (
    name: StorageKeyT,
    value: string,
    options: CookieOptionsT = {},
): void => {
    const formattedOptions: CookieOptionsT = {
        path: '/',
        ...options,
    };

    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

    if (formattedOptions.expires) {
        cookieString += `; expires=${formattedOptions.expires}`;
    } else if (formattedOptions.maxAge) {
        cookieString += `; max-age=${formattedOptions.maxAge}`;
    }

    if (formattedOptions.path) {
        cookieString += `; path=${formattedOptions.path}`;
    }

    if (formattedOptions.domain) {
        cookieString += `; domain=${formattedOptions.domain}`;
    }

    if (formattedOptions.secure) {
        cookieString += '; secure';
    }

    if (formattedOptions.samesite) {
        cookieString += `; samesite=${formattedOptions.samesite}`;
    }

    document.cookie = cookieString;
};

export const getCookie = (name: StorageKeyT): string | null => {
    const matches = document.cookie.match(
        new RegExp(
            `(?:^|; )${name.replaceAll(/([.$?*|{}()+^])/g, String.raw`\$1`)}=([^;]*)`,
        ),
    );
    return matches ? decodeURIComponent(matches[1]) : null;
};

export const deleteCookie = (name: string): void => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const getStorageItem = <ReturnType = string>(
    name: StorageKeyT,
): ReturnType | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const storageData: string | null = sessionStorage.getItem(name);

    if (
        !storageData ||
        ['null', 'undefined', '', '{}', 'false'].includes(storageData)
    ) {
        return null;
    }

    try {
        return JSON.parse(storageData);
    } catch {
        return null;
    }
};

export const setStorageItem = <T = unknown>(
    itemName: StorageKeyT,
    data?: T | null,
): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        if (!data) {
            sessionStorage.removeItem(itemName);
            return true;
        }

        sessionStorage.setItem(itemName, JSON.stringify(data));
        return true;
    } catch {
        sessionStorage.removeItem(itemName);
        return false;
    }
};

export const getLocalStorageItem = <ReturnType = string>(
    name: StorageKeyT,
): ReturnType | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    const storageData: string | null = localStorage.getItem(name);

    if (
        !storageData ||
        ['null', 'undefined', '', '{}', 'false'].includes(storageData)
    ) {
        return null;
    }

    try {
        return JSON.parse(storageData);
    } catch {
        return null;
    }
};

export const setLocalStorageItem = <T = unknown>(
    itemName: StorageKeyT,
    data?: T | null,
): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    try {
        if (!data) {
            localStorage.removeItem(itemName);
            return true;
        }

        localStorage.setItem(itemName, JSON.stringify(data));
        return true;
    } catch {
        localStorage.removeItem(itemName);
        return false;
    }
};

export const removeLocalStorageItem = (itemName: StorageKeyT): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    const storageData: string | null = localStorage.getItem(itemName);

    if (
        !storageData ||
        ['null', 'undefined', '', '{}', 'false'].includes(storageData)
    ) {
        return false;
    }

    localStorage.removeItem(itemName);
    return true;
};
